// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useSimsStore from '@/app/store/modules/sims';
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useCustomPayStore from '@/app/store/customPay';
import actionsSims from '../../api/actionsSims';
// Components
import DataTable from '@/common-utils/components/renders/dataTable/DataTable';
// Common
import simCardFields from '../../fields/simCardFields';
import getInitialQuery from '@/common-utils/components/renders/dataTable/utils/getInitialQuery';
import mergeQuery from '@/common-utils/components/renders/dataTable/utils/mergeQuery';
import { columnsSimCards, columnsSimCardsId } from './columnDef/columnsSimCards';
import Permissions from '@/app/permissions/Permissions';
import SIM_CARDS from '../../api/apiUrls';
import SimImportProps from '../import/SimImportProps';

/**
 * @param root0
 * @param root0.organizationId
 * @param root0.pageActions
 * @param root0.customQuery
 * @param root0.rowsSelected
 * @param root0.setRowsSelected
 * @param root0.singleSelection
 * @param root0.isFromModal
 * @param root0.filtersEventName
 * @returns {JSX} SimCardsTable
 */
function SimCardsTable({
    organizationId = undefined,
    pageActions = undefined,
    customQuery = {},
    rowsSelected = undefined,
    setRowsSelected = undefined,
    singleSelection = false,
    isFromModal = false,
    filtersEventName
}) {
    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const queryStored = useSimsStore(s => isFromModal ? s.querySimCardsFromModal : s.querySimCards);
    const storeQuerySimCards = useSimsStore(s => isFromModal ? s.storeQuerySimCardsFromModal : s.storeQuerySimCards);
    const refetchFromStore = useSimsStore(isFromModal ? s => s.refetchSimCardsFromModal : s => s.refetchSimCards);
    const storeRefetchFromStore = useSimsStore(isFromModal ? s => s.storeRefetchSimCardsFromModal : s => s.storeRefetchSimCards);
    const isCustomPay = useCustomPayStore(s => s.isCustomPay);

    // Constants
    const selectionMode = !!(rowsSelected && setRowsSelected);
    const QUERY_PRESETS = {
        filters: [
            {
                key: simCardFields.PROVIDER.key,
                value: organizationId || rootOrganization.uuid,
                hidden: true
            },
            ...customQuery.filters || []
        ]
    };
    const initialQuery = getInitialQuery(QUERY_PRESETS);

    // State
    const [query, setQuery] = useState(mergeQuery(initialQuery, queryStored));
    const [launchRefetch, setLaunchRefetch] = useState(true);

    // Api Calls
    const { data, isFetching, error, refetch } = actionsSims.getSimCards(query, false);

    // Hooks
    useEffect(() => {
        if (rootOrganization && (launchRefetch || refetchFromStore)) {
            refetch();
            setLaunchRefetch(false);
            storeRefetchFromStore(false);
        }
    }, [query, launchRefetch, refetchFromStore]);

    const allColumns = columnsSimCards({
        query,
        setQuery,
        queryStored,
        storeQuerySimCards,
        setLaunchRefetch,
        selectionMode,
        singleSelection,
        filtersEventName
    },
        isCustomPay);

    // Render
    return (
        <DataTable
            filtersEventName={filtersEventName}
            columns={allColumns}
            data={data}
            dataFetchingIsLoading={isFetching}
            error={error}
            refetch={refetch}
            columnsId={columnsSimCardsId}
            initialQuery={initialQuery}
            query={query}
            setQuery={setQuery}
            queryStored={queryStored}
            storeQuery={storeQuerySimCards}
            setLaunchRefetch={setLaunchRefetch}

            selectionMode={selectionMode}
            singleSelection={singleSelection}
            rowsSelected={rowsSelected}
            setRowsSelected={setRowsSelected}

            pageActions={pageActions}
            exportProps={!organizationId ?
                {
                    permissions: [
                        Permissions.EXPORT_SIMS.name
                    ],
                    api: SIM_CARDS.EXPORT
                } : null
            }
            importProps={isCustomPay ? {
                ...SimImportProps,
                setLaunchRefetch,
                permissions: [Permissions.SIM_IMPORT_CSV.name]
            } : null}
        />
    );
}

SimCardsTable.propTypes = {
    organizationId: PropTypes.string,
    pageActions: PropTypes.node,
    customQuery: PropTypes.object,
    rowsSelected: PropTypes.object,
    setRowsSelected: PropTypes.func,
    singleSelection: PropTypes.bool,
    filtersEventName: PropTypes.string,
    isFromModal: PropTypes.bool
};
export default SimCardsTable;